<script lang="ts" setup>
import type { LogicalFilter } from '#layers/data-model/public';
import type { FilterSchemaCollection } from './utils/filter-schema';
import { nextTick, ref, watch } from 'vue';
import { isEmptyFilter } from '#layers/data-model/public';
import { ButtonRemove } from '#layers/ui/public';

const props = defineProps<{
	collection: FilterSchemaCollection;
	/** A group is added empty, so advancing into one means opening it — its conditions live in here. */
	autoAdvance?: boolean;
}>();

const emit = defineEmits<{
	remove: [];
	autoAdvanceDone: [];
}>();

const filter = defineModel<LogicalFilter>({ required: true });

const open = ref(false);

// Reporting back lets the bar drop the request, so the next group added in front of this one — the
// clause list is keyed by index, so it lands on this same instance — flips the prop and reopens.
// The emit waits a tick so the bar isn't asked to change while this group is mid-render.
watch(() => props.autoAdvance, async (active) => {
	if (!active) return;

	open.value = true;
	await nextTick();
	emit('autoAdvanceDone');
}, { immediate: true });

// The bar keys its clause list by index, so this instance outlives the group it shows: leaving the
// popover open would hand it to whichever group shifts into the slot.
function onRemove() {
	open.value = false;
	emit('remove');
}
</script>

<template>
	<DPopover
		v-model:open="open"
		:content="{ side: 'bottom', align: 'start', updatePositionStrategy: 'always' }"
		:ui="{ content: 'bg-default p-2.5' }"
	>
		<FilterHandleGroup>
			<DButton
				color="neutral"
				variant="ghost"
				size="sm"
				class="flex gap-3 ps-3"
			>
				<DChip v-if="!isEmptyFilter(filter)" size="sm" :ui="{ base: 'ring-0' }" />
				<span class="whitespace-nowrap">{{ $t('filters.advanced_filter') }}</span>
			</DButton>
			<FilterRemove @remove="onRemove()" />
		</FilterHandleGroup>
		<template #content>
			<div class="flex flex-col gap-2">
				<FilterLogicalGroup
					v-model="filter"
					:collection
					:hide-group-actions="false"
				/>
				<DSeparator />
				<ButtonRemove
					class="text-muted"
					with-leading-icon
					:label="$t('filters.remove_filter')"
					@click="onRemove()"
				/>
			</div>
		</template>
	</DPopover>
</template>
